import React, { useState, useEffect } from 'react';

import { Grid, Accordion, AccordionSummary, AccordionDetails, Link } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CollapseIcon from '@material-ui/icons/ExpandMore';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import FileUploadSidePanel from 'components/FileUploadSidePanel/FileUploadSidePanel';
import HighlightedText from 'components/HighlightedText/HighlightedText';
import MeshGenericTable from 'components/MeshGenericTable/MeshGenericTable';
import { useDispatch } from 'react-redux';
import { VIEWS_ACTIONS, resetViewsPanel, setShowViewsPanel, setViewAction } from 'reducers/viewsSlice';
import ActionsContainer from 'screens/admin/Reviews/ReviewProcess/ReviewCycle/actionsContainer';
import { useCohortCreationSidePanelControl } from 'screens/common/CohortCreation/CohortSidePanelControlContext';
import styled, { css } from 'styled-components';
import { NoResultsText } from 'styles/admin';
import {
  StyledButton,
  StyledGrid,
  StyledIconContainer,
  StyledPaper,
  StyledTypography,
  VerticallyCenterContainer,
  BackgroundCSS,
  FlexContainer,
  ColoredCircularProgress,
  StyledBox,
} from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { sampleEmployeeSelectionFileURI } from 'constants/ProjectConstants';
import { getColorFromKey } from 'constants/helper';
import theme from 'constants/theme';

import { uploadEmployeesCSV, getParticipants, putParticipantsCohort } from 'services/engagement';
import { showErrorSnackbar } from 'services/snackbar';

import { DeerFooterContainer } from '../DeerFooterContainer';

const ROWS_PER_PAGE = 5;
const PARTICIPANT_SELECTION_TYPE = {
  COHORT: 'COHORT',
  FILE: 'FILE',
};

const SurveyParticipants = ({ formId, backFn, nextFn }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(null);
  const [openUploaderSidebar, setOpenUploaderSidebar] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [formData, setFormData] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [allCohorts, setAllCohorts] = useState(null);
  const [cohortExpanded, setCohortExpanded] = useState(false);
  const { showCohortCreationSidepanel } = useCohortCreationSidePanelControl();

  const isNextDisabled = () => {
    if (selectedOption) {
      if (selectedOption === PARTICIPANT_SELECTION_TYPE.COHORT) {
        return !formData?.selectedCohortId || !formData?.revieweesCount;
      }
      if (selectedOption === PARTICIPANT_SELECTION_TYPE.FILE) {
        return !formData?.revieweesCount;
      }
    }
    return true;
  };

  // Pagination States
  const [page, setPage] = useState(0);
  const [filteredData, setFilteredData] = useState(null);

  useEffect(() => {
    if (allCohorts && Array.isArray(allCohorts)) {
      filterData(allCohorts, page);
    }
  }, [allCohorts, page]);

  const filterData = (data, page) => {
    const result = [...data];
    setFilteredData(result.filter((r, i) => i >= page * ROWS_PER_PAGE && i < (page + 1) * ROWS_PER_PAGE));
  };

  const onCohortCreationSuccess = (cohort) => {
    addCohort({ cohort });
    onCohortSelect(cohort);
    dispatch(resetViewsPanel());
  };

  const handleCreateCohortClick = () => {
    dispatch(setViewAction(VIEWS_ACTIONS.CREATE));
    dispatch(
      setShowViewsPanel({
        onSuccess: onCohortCreationSuccess,

        constraints: {
          disallowPrivate: true,
          source: 'ENGAGEMENT',
          allowOnlyMyCompany: true,
          disableApply: true,
        },
      })
    );
  };

  const handleCohortAccordionChange = () => {
    setCohortExpanded(!cohortExpanded);
    onSelect(PARTICIPANT_SELECTION_TYPE.COHORT);
  };

  const handleOnCancel = () => {
    setOpenUploaderSidebar(false);
  };

  const handleOnSuccess = ({ res }) => {
    setOpenUploaderSidebar(false);
    getData();
    setSelectedCohort(null);
  };

  const onSelect = (id) => {
    if (selectedOption === id) return;

    setFormData({
      ...formData,
      revieweesCount: 0,
      selectedCohortId: null,
    });
    setSelectedCohort(null);

    setSelectedOption(id);
  };

  const saveSelectedCohort = ({ formId, cohortId }) => {
    onSelect(PARTICIPANT_SELECTION_TYPE.COHORT);

    putParticipantsCohort({ surveyId: formId, cohortId })
      .then((res) => {
        getData();
      })
      .catch((err) => dispatch(showErrorSnackbar('Oops! Something went wrong')));
  };

  const onCohortSelect = (cohort) => {
    setLoading(true);
    setSelectedCohort(cohort);
    saveSelectedCohort({ formId, cohortId: cohort.id });
    setLoading(false);
  };

  const addCohort = ({ cohort }) => {
    const updateCohortData = () => [cohort, ...allCohorts];
    setAllCohorts(updateCohortData());
  };

  const getData = () => {
    setLoading(true);
    getParticipants({ surveyId: formId })
      .then((res) => {
        setAllCohorts([
          ...res.cohorts.filter((item) => item.id === res.selectedCohortId),
          ...res.cohorts.filter((item) => item.id !== res.selectedCohortId),
        ]);
        setFormData(res);
        if (res.selectedCohortId) {
          setSelectedOption(PARTICIPANT_SELECTION_TYPE.COHORT);
          setSelectedCohort(res.cohorts.filter((item) => item.id === res.selectedCohortId)[0]);
        } else if (res.revieweesCount >= 1) {
          setSelectedOption(PARTICIPANT_SELECTION_TYPE.FILE);
        }
        setPage(0);
        setLoading(false);
      })
      .catch((err) => dispatch(showErrorSnackbar('Sorry, unable to fetch the participants data!')));
  };

  useEffect(() => {
    getData();
  }, []);

  const save = () => {
    setLoading(true);
    nextFn();
  };

  const cohortHeaderList = [
    {
      id: 1,
      space: 3,
      comp: () => (
        <StyledFlexContainer>
          <StyledTypography>
            <HighlightedText
              text={formData?.selectedCohortId ? formData?.revieweesCount : '-'}
              color="blue"
              bgColor="lightBlue"
            />
          </StyledTypography>
          <StyledTypography color="textSecondary">Total Employees Selected</StyledTypography>
        </StyledFlexContainer>
      ),
    },
    {
      id: 2,
      space: 3,
      comp: () => (
        <StyledButton color="primary" variant="outlined" fullWidth onClick={handleCreateCohortClick}>
          Create New Cohort
        </StyledButton>
      ),
    },
  ];

  const cohortSecondaryHeaderList = [
    {
      id: 1,
      spacing: 3,
      comp: () => <StyledTypography>Cohort Name</StyledTypography>,
    },
    // {
    //   id: 2,
    //   spacing: 3,
    //   comp: () => <StyledTypography>Number of Employees</StyledTypography>
    // },
    {
      id: 3,
      spacing: 6,
      comp: () => '',
    },
  ];

  const cohortRowComponent = (cohort) => {
    const { id, name } = cohort;
    return {
      id,
      comp: (
        <StyledContainerGrid
          container
          justify="space-between"
          alignItems="center"
          onClick={() => onCohortSelect(cohort)}
        >
          <Grid item sm={6}>
            <StyledTypography variant="body1" />
            <VerticallyCenterContainer>
              <StyledIconContainer size="big">
                {/* For Later wen we support multiple cohort selection */}
                {/* {selectedCohort?.id === id ? (
                  <BlueCheckBoxIcon />
                ) : (
                  <BlueCheckBoxOutlineBlankIcon />
                )} */}

                {selectedCohort?.id === id ? <BlueRadioButtonCheckedIcon /> : <BlueRadioButtonUncheckedIcon />}
              </StyledIconContainer>
              {name}
            </VerticallyCenterContainer>
          </Grid>
          {/* <Grid item sm={3}>
            <StyledTypography color="textSecondary" variant="subtitle2">
              employeesCount
            </StyledTypography>
          </Grid> */}
          <Grid item sm={3}>
            <StyledFlexContainer>
              {/* <StyledIconButton>
                <KeyboardArrowRightIcon />
              </StyledIconButton> */}
            </StyledFlexContainer>
          </Grid>
        </StyledContainerGrid>
      ),
    };
  };

  const listData = () => filteredData.map((cohort) => cohortRowComponent(cohort));

  return (
    <>
      <StyledTypography variant="h5" color="textPrimary" bottomGutter={1}>
        Select Participants
      </StyledTypography>
      <StyledTypography variant="body" color="textSecondary">
        Select employees to fill the survey.
      </StyledTypography>
      {loading === false ? (
        <FormContainer>
          <StyledAccordion expanded={cohortExpanded} onChange={handleCohortAccordionChange}>
            <StyledAccordionSummary
              bgColor={
                selectedOption === PARTICIPANT_SELECTION_TYPE.COHORT ? theme.palette.background.blue.light.v2 : 'white'
              }
              expandIcon={<CollapseIcon />}
            >
              <StyledGrid container justify="space-between" alignItems="center">
                <StyledGrid item sm={6}>
                  <StyledTypography>
                    <VerticallyCenterContainer>
                      <StyledIconContainer size="big">
                        {selectedOption === PARTICIPANT_SELECTION_TYPE.COHORT ? (
                          <BlueRadioButtonCheckedIcon />
                        ) : (
                          <BlueRadioButtonUncheckedIcon />
                        )}
                      </StyledIconContainer>
                      Select Cohort
                    </VerticallyCenterContainer>
                  </StyledTypography>
                </StyledGrid>
                <StyledGrid item sm={3}>
                  {formData?.selectedCohortId
                    ? `${formData?.revieweesCount} Employees Selected`
                    : 'No Employees Selected'}
                </StyledGrid>
                <StyledGrid item sm={3}>
                  <StyledBox flexcontainer justifycontent="center">
                    {formData?.selectedCohortId
                      ? allCohorts.filter((cohort) => cohort.id === formData.selectedCohortId)[0]?.name
                      : '-'}
                  </StyledBox>
                </StyledGrid>
              </StyledGrid>
            </StyledAccordionSummary>
            <AccordionDetails>
              {filteredData && (
                <MeshGenericTable
                  loading={loading}
                  listData={listData()}
                  headerList={cohortHeaderList} // todo: move header list which is used to create cohort outside of table component
                  secondaryHeaderList={cohortSecondaryHeaderList}
                  wrapperPaperProps={{
                    bottomGutter: 0,
                    elevation: 0,
                  }}
                  dataRowPadding={0}
                  clickableRows
                  paginationData={{
                    count: allCohorts?.length,
                    rowsPerPage: ROWS_PER_PAGE,
                    setCurrentPage: (page) => {
                      setPage(page);
                    },
                    currentPage: page,
                  }}
                />
              )}
            </AccordionDetails>
          </StyledAccordion>
          <StyledPaper
            bgColor={
              selectedOption === PARTICIPANT_SELECTION_TYPE.FILE ? theme.palette.background.blue.light.v2 : 'white'
            }
            bottomGutter={2}
            padding="0.5rem 2rem"
            clickable
            onClick={() => onSelect(PARTICIPANT_SELECTION_TYPE.FILE)}
          >
            <StyledGrid container justify="space-between" alignItems="center">
              <StyledGrid item sm={6}>
                <StyledTypography>
                  <VerticallyCenterContainer>
                    <StyledIconContainer size="big">
                      {selectedOption === PARTICIPANT_SELECTION_TYPE.FILE ? (
                        <BlueRadioButtonCheckedIcon />
                      ) : (
                        <BlueRadioButtonUncheckedIcon />
                      )}
                    </StyledIconContainer>
                    Upload Employees List
                  </VerticallyCenterContainer>
                </StyledTypography>
              </StyledGrid>
              <StyledGrid item sm={3}>
                {formData?.selectedCohortId
                  ? 'No Employees Selected'
                  : `${formData?.revieweesCount} Employees Selected`}
              </StyledGrid>
              <StyledGrid item sm={3}>
                <StyledButton onClick={() => setOpenUploaderSidebar(true)} fullWidth color="primary" variant="outlined">
                  Upload CSV
                </StyledButton>
              </StyledGrid>
            </StyledGrid>
          </StyledPaper>
        </FormContainer>
      ) : (
        <NoResultsText fullHeight>
          <ColoredCircularProgress size="tiny" />
        </NoResultsText>
      )}
      <ActionsContainer
        ContainerComp={checkNewNavEnabled() ? DeerFooterContainer : null}
        onBackClick={backFn}
        onNextClick={nextFn && save}
        loading={loading}
        nextDisabled={isNextDisabled()}
      />
      <FileUploadSidePanel
        helperText={
          <>
            {' '}
            <Link download="CSVSample.csv" href={sampleEmployeeSelectionFileURI} color="primary">
              Download
            </Link>
            {` our sample CSV file to refer to the upload format.`}
          </>
        }
        uploadHandlerService={uploadEmployeesCSV}
        open={openUploaderSidebar}
        onCancel={handleOnCancel}
        onSuccess={handleOnSuccess}
        uploadHandlerServiceAdditionalParams={{
          surveyId: formId,
        }}
      />
    </>
  );
};

const FormContainer = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 5rem;
`;

const BlueRadioButtonCheckedIcon = styled(RadioButtonCheckedIcon)`
  color: ${getColorFromKey('primary')};
`;

const BlueRadioButtonUncheckedIcon = styled(RadioButtonUncheckedIcon)`
  color: ${getColorFromKey('primary')};
`;

const BlueCheckBoxIcon = styled(CheckBoxIcon)`
  color: ${getColorFromKey('primary')};
`;

const BlueCheckBoxOutlineBlankIcon = styled(CheckBoxOutlineBlankIcon)`
  color: ${getColorFromKey('primary')};
`;

const StyledAccordion = styled(Accordion)`
  background: white;
  margin-bottom: 2rem;
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  .MuiAccordionSummary-content {
    margin: 0;
  }
  box-shadow: none;
  padding: 0.5rem 2rem;
  ${BackgroundCSS}
`;

const StyledFlexContainer = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
`;

const StyledContainerGrid = styled(Grid)`
  &:hover ${StyledFlexContainer} {
    visibility: visible;
  }
`;

const StyledCollapseIcon = styled(CollapseIcon)`
  ${(props) =>
    props.open &&
    css`
      transform: rotate(180deg);
    `}
`;

export default SurveyParticipants;
